import { useMemo, useState } from "react";
import {
  Button,
  Callout,
  EmptyState,
  Icon,
  Rule,
  SectionHeader,
  Spinner,
  Stamp,
  Stat,
  Tag,
} from "../ui";
import { downloadFinalExport } from "../api/monograph";
import { useMonograph } from "../hooks/useMonograph";
import type { FinalExportFormat, MonographSection, Project } from "../types";

export interface Step8DownloadProps {
  project: Project;
}

type ExportAction = FinalExportFormat | "copy";

const MANUAL_FIELDS = [
  "perigos identificados",
  "calculo de PDE",
  "POD",
  "fatores F1-F5",
  "CV e demais campos de julgamento toxicologico",
];

function safeStem(value: string): string {
  return value
    .trim()
    .replace(/[^A-Za-z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase() || "monografia";
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function sectionTitle(section: MonographSection): string {
  return section.title_pt || section.title_en || section.label;
}

function sectionText(section: MonographSection): string {
  return section.final_text_pt?.trim() || "";
}

function copyableText(sections: MonographSection[]): string {
  return sections
    .map((section) => {
      const title = sectionTitle(section);
      const text = sectionText(section);
      return `${title}\n\n${text}`;
    })
    .join("\n\n");
}

export function Step8Download({ project }: Step8DownloadProps) {
  const monographQuery = useMonograph(project.id);
  const [pending, setPending] = useState<ExportAction | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const sections = useMemo(
    () => [...(monographQuery.data ?? [])].sort((a, b) => a.section_order - b.section_order),
    [monographQuery.data],
  );
  const populated = sections.filter((section) => sectionText(section));
  const empty = sections.length - populated.length;
  const ready = populated.length > 0;
  const filenameStem = safeStem(project.drug_api_name);
  const generatedLabel = new Date().toLocaleDateString("pt-BR");

  const exportFile = async (format: FinalExportFormat) => {
    setPending(format);
    setError(null);
    try {
      const blob = await downloadFinalExport(project.id, format);
      downloadBlob(blob, `${filenameStem}-final-pt.${format}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao baixar o arquivo final.");
    } finally {
      setPending(null);
    }
  };

  const copyText = async () => {
    setPending("copy");
    setError(null);
    try {
      await navigator.clipboard.writeText(copyableText(sections));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao copiar o texto.");
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="anim-fade">
      <SectionHeader
        number={8}
        eyebrow="Etapa 08 · Entrega"
        title="Documento final"
        lede="Prévia da monografia PT-BR persistida e downloads finais autenticados do DOCX Altox e TXT de apoio."
      />

      <div className="mb-8">
        <Callout tone="strike" icon={Icon.AlertTriangle} title="Campos manuais permanecem no Word">
          O DOCX final preserva os campos amarelos para o toxicologista preencher:{" "}
          {MANUAL_FIELDS.join(", ")}.
        </Callout>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border-y-2 border-[var(--ink)] mb-8">
        <div className="px-6 py-5 border-r border-[var(--rule)]">
          <Stat value={sections.length || 16} label="Seções" size="md" />
        </div>
        <div className="px-6 py-5 lg:border-r border-[var(--rule)]">
          <Stat value={populated.length} label="Preenchidas" size="md" />
        </div>
        <div className="px-6 py-5 border-t lg:border-t-0 border-r border-[var(--rule)]">
          <Stat value={empty} label="Vazias" size="md" />
        </div>
        <div className="px-6 py-5 border-t lg:border-t-0 border-[var(--rule)]">
          <Stat value={project.status} label="Status" size="md" />
        </div>
      </div>

      <div className="border border-[var(--ink)] p-8 mb-10 bg-[var(--paper-2)]">
        <div className="flex items-start justify-between gap-6 flex-wrap mb-7">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <Stamp tone={ready ? "ok" : "ink"}>{ready ? "Pronto" : "Aguardando build"}</Stamp>
              <Stamp tone="ink" className="rotate-1">Altox · v1</Stamp>
            </div>
            <h2 className="display-3 leading-none mb-2">{project.drug_api_name}</h2>
            <p className="caption">
              CAS {project.cas_number ?? "-"} · {project.molecular_formula ?? "-"} ·{" "}
              {project.molecular_weight ?? "-"} · {generatedLabel}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Button
              variant="ink"
              size="sm"
              icon={pending === "docx" ? Icon.Loader : Icon.Download}
              onClick={() => exportFile("docx")}
              disabled={!ready || pending !== null}
            >
              Baixar DOCX (Altox)
            </Button>
            <Button
              variant="line"
              size="sm"
              icon={pending === "txt" ? Icon.Loader : Icon.Download}
              onClick={() => exportFile("txt")}
              disabled={!ready || pending !== null}
            >
              Baixar TXT
            </Button>
            <Button
              variant="ghost"
              size="sm"
              icon={copied ? Icon.Check : pending === "copy" ? Icon.Loader : Icon.Copy}
              onClick={copyText}
              disabled={!ready || pending !== null}
            >
              {copied ? "Copiado" : "Copiar texto"}
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-6">
            <Callout tone="strike" icon={Icon.AlertCircle} title="Exportação indisponível">
              {error}
            </Callout>
          </div>
        )}

        {monographQuery.isPending && (
          <div className="caption flex items-center gap-3">
            <Spinner /> Carregando monografia final...
          </div>
        )}
        {monographQuery.isError && (
          <Callout tone="strike" icon={Icon.AlertCircle} title="Prévia indisponível">
            Não foi possível carregar as seções finais. Recarregue a página ou tente novamente.
          </Callout>
        )}
        {!monographQuery.isPending && !monographQuery.isError && !ready && (
          <EmptyState
            icon={Icon.FileText}
            title="Monografia PT-BR ainda não compilada"
            description="Volte à etapa de tradução e compile a monografia PT-BR antes de baixar o documento final."
          />
        )}
      </div>

      {ready && (
        <>
          <Rule className="mb-5">§ 8.1 · Prévia PT-BR</Rule>
          <div className="anim-stagger">
            {sections.map((section) => {
              const text = sectionText(section);
              return (
                <article key={section.id} className="py-6 border-b border-[var(--rule-2)]">
                  <div className="flex items-baseline justify-between gap-4 mb-3">
                    <div className="flex items-baseline gap-3">
                      <span className="step-num text-[20px] tabular text-[var(--ink-4)]">
                        {String(section.section_order).padStart(2, "0")}
                      </span>
                      <h3 className="display-5">{sectionTitle(section)}</h3>
                    </div>
                    <Tag tone={text ? "ok" : "rule"}>{text ? "Preenchida" : "Vazia"}</Tag>
                  </div>
                  {text ? (
                    <div className="space-y-3">
                      {text.split("\n\n").map((paragraph, index) => (
                        <p
                          key={`${section.id}-${index}`}
                          className="serif text-[15.5px] leading-[1.65] text-[var(--ink)]"
                        >
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  ) : (
                    <p className="caption italic">Sem texto automático para esta seção.</p>
                  )}
                </article>
              );
            })}
          </div>
          <div className="mt-10 pt-6 border-t-2 border-[var(--ink)] flex items-center justify-end gap-2 flex-wrap">
            <Button
              variant="ink"
              size="sm"
              icon={pending === "docx" ? Icon.Loader : Icon.Download}
              onClick={() => exportFile("docx")}
              disabled={!ready || pending !== null}
            >
              Baixar DOCX (Altox)
            </Button>
            <Button
              variant="line"
              size="sm"
              icon={pending === "txt" ? Icon.Loader : Icon.Download}
              onClick={() => exportFile("txt")}
              disabled={!ready || pending !== null}
            >
              Baixar TXT
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
